import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { LifecycleFocus, splitRegisteredName } from '../lifecycle';
import { Badge, Button, Card, EmptyState, Field, inputStyle, tokens } from './ui/controls';

type ModelVersion = { version: string; aliases: string[]; tags: Record<string, string> };

type ModelRow = {
  name: string;
  deploy_location?: string;
  aliases: Record<string, string>;
  versions: ModelVersion[];
};

function aliasTone(alias: string) {
  const value = alias.toLowerCase();
  if (value === 'champion') return 'success';
  if (value === 'challenger') return 'warning';
  if (value.includes('champion')) return 'neutral';
  return 'info';
}

function stageTone(stage: string) {
  const value = stage.toLowerCase();
  if (value.includes('not convertable') || value.includes('failed')) return 'danger';
  if (value.includes('deployed')) return 'success';
  if (value.includes('deployable') || value.includes('convertable')) return 'info';
  if (value.includes('testing') || value.includes('checked')) return 'warning';
  return 'neutral';
}

function versionSort(a: ModelVersion, b: ModelVersion) {
  return Number(b.version) - Number(a.version);
}

type Inspection = {
  model_name: string;
  version: string;
  check_status: string;
  model_classes: Record<string, string>;
  expected_classes: Record<string, string | null>;
  issues: string[];
};

export default function ModelRegistryTab({
  focus,
  onFocusConsumed,
  onTest,
  onInfer,
}: {
  focus: LifecycleFocus | null;
  onFocusConsumed: () => void;
  onTest: (species: string, modelType: string, version: string) => void;
  onInfer: (modelName: string, version: string) => void;
}) {
  const [models, setModels] = useState<ModelRow[]>([]);
  const [pending, setPending] = useState<Array<Record<string, unknown>>>([]);
  const [status, setStatus] = useState('');
  const [comment, setComment] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [inspecting, setInspecting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.dataviewer.modelRegistry();
      const data = res.data as { models: ModelRow[]; pending: Array<Record<string, unknown>> };
      setModels(data.models || []);
      setPending(data.pending || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh().catch((e) => setStatus(String(e))); }, [refresh]);

  const inspect = useCallback(async (modelName: string, version: string) => {
    setInspecting(true);
    setStatus('');
    try {
      const res = await api.dataviewer.inspectModel({ model_name: modelName, version });
      setInspection(res.data);
      setFilter(modelName);
    } catch (e: any) {
      setInspection(null);
      setStatus(e.response?.data?.detail || e.message || 'Inspect failed');
    } finally {
      setInspecting(false);
    }
  }, []);

  useEffect(() => {
    if (!focus || focus.tab !== 'models') return;
    setFilter(focus.modelName);
    if (focus.inspect) {
      inspect(focus.modelName, focus.version).catch(() => undefined);
    }
    onFocusConsumed();
  }, [focus, inspect, onFocusConsumed]);

  const run = async (action: Promise<unknown>) => {
    setStatus('');
    try {
      await action;
      await refresh();
    } catch (e: any) {
      setStatus(e.response?.data?.detail || e.message || 'Request failed');
    }
  };

  const visibleModels = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matched = needle ? models.filter((m) => m.name.toLowerCase().includes(needle)) : models;
    return [...matched].sort((a, b) => a.name.localeCompare(b.name));
  }, [models, filter]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Model registry</h2>
      <p style={{ marginTop: 0, fontSize: '0.88rem', color: tokens.textMuted, maxWidth: 720 }}>
        Versions and aliases are read from the database registry. Sync copies the MLflow registry in while both are still in use.
        Champion and challenger requests from this UI need a second approver.
      </p>

      {inspection ? (
        <Card title={`Inspect ${inspection.model_name} v${inspection.version}`}>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <Badge tone={inspection.check_status === 'passed' ? 'success' : 'danger'}>
              {inspection.check_status}
            </Badge>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong style={{ fontSize: '0.85rem' }}>Found</strong>
                {Object.entries(inspection.model_classes).map(([index, name]) => (
                  <div key={index} style={{ fontSize: '0.82rem', color: tokens.textMuted }}>{index}: {name}</div>
                ))}
              </div>
              <div>
                <strong style={{ fontSize: '0.85rem' }}>Expected</strong>
                {Object.entries(inspection.expected_classes).map(([index, name]) => (
                  <div key={index} style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
                    {index}: {name ?? '*'}
                  </div>
                ))}
              </div>
            </div>
            {inspection.issues.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.82rem', color: tokens.danger }}>
                {inspection.issues.map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            ) : null}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <Button
                variant="primary"
                disabled={inspecting || inspection.check_status !== 'passed'}
                onClick={() => run(api.dataviewer.promoteModel({
                  model_name: inspection.model_name,
                  version: inspection.version,
                  stage: 'Checked',
                }))}
              >
                Mark checked
              </Button>
              <Button
                disabled={inspecting || inspection.check_status === 'passed'}
                onClick={() => run(api.dataviewer.promoteModel({
                  model_name: inspection.model_name,
                  version: inspection.version,
                  stage: 'Not Convertable',
                }))}
              >
                Mark not convertable
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card
        title="Pending approvals"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <Badge tone={pending.length > 0 ? 'warning' : 'neutral'}>{pending.length}</Badge>
            <Button onClick={() => run(api.dataviewer.syncRegistry())} disabled={loading}>
              Sync from MLflow
            </Button>
            <Button onClick={() => refresh().catch((e) => setStatus(String(e)))} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <Field label="Review comment">
            <input
              style={inputStyle}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why this alias moves…"
            />
          </Field>

          {pending.length === 0 ? (
            <EmptyState>No alias requests waiting for review.</EmptyState>
          ) : (
            <div style={{ display: 'grid', gap: '0.4rem' }}>
              {pending.map((row) => (
                <div
                  key={String(row.id)}
                  style={{
                    border: `1px solid ${tokens.border}`,
                    borderRadius: tokens.radius,
                    padding: '0.6rem 0.8rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <Badge tone={aliasTone(String(row.alias))}>{String(row.alias)}</Badge>
                    <strong style={{ fontSize: '0.92rem' }}>{String(row.model_name)}</strong>
                    <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>v{String(row.version)}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: tokens.textMuted, marginTop: '0.3rem' }}>
                    requested by {String(row.requester)}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                    <Button
                      variant="primary"
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                      onClick={() => run(api.dataviewer.reviewAlias(Number(row.id), { decision: 'approve', comment }))}
                    >
                      Approve
                    </Button>
                    <Button
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                      onClick={() => run(api.dataviewer.reviewAlias(Number(row.id), { decision: 'reject', comment }))}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {status && <p style={{ fontSize: '0.85rem', color: tokens.danger }}>{status}</p>}

      <Card
        title="Registered models"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
              {visibleModels.length} of {models.length}
            </span>
            <input
              style={{ ...inputStyle, width: 'auto', minWidth: 200 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by model name…"
            />
          </div>
        }
      >
        {visibleModels.length === 0 ? (
          <EmptyState>{models.length === 0 ? 'No models in the registry.' : 'No models match that filter.'}</EmptyState>
        ) : (
          <div style={{ maxHeight: 620, overflow: 'auto', display: 'grid', gap: '0.6rem' }}>
            {visibleModels.map((model) => (
              <div
                key={model.name}
                style={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: tokens.radius,
                  padding: '0.6rem 0.8rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '0.92rem' }}>{model.name}</strong>
                  {model.deploy_location ? <Badge tone="info">{model.deploy_location}</Badge> : null}
                  {Object.entries(model.aliases).map(([alias, version]) => (
                    <Badge key={alias} tone={aliasTone(alias)}>{alias} v{version}</Badge>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: '0.3rem', marginTop: '0.5rem' }}>
                  {[...model.versions].sort(versionSort).map((v) => {
                    const stage = v.tags.lifecycle_stage || '';
                    return (
                      <div
                        key={v.version}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                          padding: '0.3rem 0',
                          borderTop: `1px solid ${tokens.border}`,
                        }}
                      >
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, minWidth: 40 }}>v{v.version}</span>
                        {v.aliases.map((alias) => (
                          <Badge key={alias} tone={aliasTone(alias)}>{alias}</Badge>
                        ))}
                        {stage ? <Badge tone={stageTone(stage)}>{stage}</Badge> : null}
                        <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                          {splitRegisteredName(model.name) ? (
                            <Button
                              style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                              onClick={() => {
                                const parsed = splitRegisteredName(model.name);
                                if (parsed) onTest(parsed.species, parsed.modelType, v.version);
                              }}
                            >
                              Test
                            </Button>
                          ) : null}
                          <Button
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            disabled={inspecting}
                            onClick={() => inspect(model.name, v.version)}
                          >
                            Inspect
                          </Button>
                          <Button
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            onClick={() => onInfer(model.name, v.version)}
                          >
                            Infer
                          </Button>
                          <Button
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            onClick={() => run(api.dataviewer.requestAlias({
                              model_name: model.name,
                              version: v.version,
                              alias: 'challenger',
                            }))}
                          >
                            Request challenger
                          </Button>
                          <Button
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            onClick={() => run(api.dataviewer.requestAlias({
                              model_name: model.name,
                              version: v.version,
                              alias: 'champion',
                            }))}
                          >
                            Request champion
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
